import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { FiAward, FiSlash, FiCalendar, FiUser, FiPrinter, FiAlertTriangle, FiEdit3, FiDownload, FiFileText } from 'react-icons/fi';

import { Spinner } from '../../components/ui/Spinner';
import { Alert } from '../../components/ui/Alert';
import { Button } from '../../components/ui/Button';
import { PortalPage } from '../../components/layout/PortalPage';
import { SectionCard } from '../../components/layout/SectionCard';
import { supabase } from '../../lib/supabase';
import { getEstabelecimentoAtualId } from '../../lib/estabelecimento';
import {
  atosOficiaisService, TIPO_ATO_LABEL,
  type AtoDetalhe, type TipoAto,
} from '../../services/atosOficiais.service';
import { formatarDataBR } from '../../lib/data';

export function AtoOficialDetalhePublicoPage() {
  const { id } = useParams<{ id: string }>();
  const [estab, setEstab] = useState<{ nome: string; cor_primaria: string | null } | null>(null);
  const [ato, setAto] = useState<AtoDetalhe | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [carregando, setCarregando] = useState(true);

  useEffect(() => {
    if (!id) return;
    setCarregando(true);
    setErro(null);
    const estabId = getEstabelecimentoAtualId();
    Promise.all([
      atosOficiaisService.buscar(id),
      supabase.from('estabelecimentos').select('nome, cor_primaria').eq('id', estabId).maybeSingle(),
    ])
      .then(([dados, e]) => {
        if ('erro' in dados) {
          setErro(mensagemErro(dados.erro));
        } else {
          setAto(dados);
        }
        if (e.data) setEstab(e.data as { nome: string; cor_primaria: string | null });
      })
      .catch((e) => setErro(e instanceof Error ? e.message : 'Erro'))
      .finally(() => setCarregando(false));
  }, [id]);

  if (carregando) {
    return (
      <PortalPage titulo="Ato oficial" icone={<FiFileText size={26} />} largura="media" voltarHref="/transparencia/atos-oficiais" voltarLabel="Voltar à lista">
        <div className="flex justify-center py-16"><Spinner tamanho="lg" /></div>
      </PortalPage>
    );
  }
  if (erro || !ato) {
    return (
      <PortalPage
        titulo="Ato oficial"
        icone={<FiFileText size={26} />}
        largura="media"
        voltarHref="/transparencia/atos-oficiais"
        voltarLabel="Voltar à lista"
        breadcrumbs={[{ label: 'Transparência' }, { label: 'Atos oficiais', href: '/transparencia/atos-oficiais' }]}
      >
        <Alert variante="erro">{erro ?? 'Ato não encontrado.'}</Alert>
      </PortalPage>
    );
  }

  const tipoLabel = ato.tipo_ato ? TIPO_ATO_LABEL[ato.tipo_ato as TipoAto] : 'Ato oficial';
  const nomeEstab = ato.estabelecimento_nome ?? estab?.nome;

  return (
    <PortalPage
      titulo={`${tipoLabel} nº ${ato.numero_publicado}`}
      subtitulo={[nomeEstab, ato.data_publicacao && `Publicado em ${formatarDataBR(ato.data_publicacao)}`].filter(Boolean).join(' · ')}
      icone={ato.revogado ? <FiSlash size={26} /> : <FiAward size={26} />}
      largura="media"
      voltarHref="/transparencia/atos-oficiais"
      voltarLabel="Voltar à lista"
      breadcrumbs={[
        { label: 'Transparência' },
        { label: 'Atos oficiais', href: '/transparencia/atos-oficiais' },
        { label: `nº ${ato.numero_publicado}` },
      ]}
      acoes={
        ato.pdf_url ? (
          <a
            href={ato.pdf_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 rounded-md bg-white/10 px-3 py-2 text-sm font-medium text-white ring-1 ring-white/20 transition hover:bg-white/20"
          >
            <FiDownload size={15} /> Baixar PDF oficial
          </a>
        ) : undefined
      }
    >
      <div className="space-y-5">
        {ato.revogado && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-900 print:hidden">
            <div className="flex items-center gap-2 font-semibold mb-1">
              <FiAlertTriangle size={14} /> Este ato foi revogado
            </div>
            <div className="text-red-800">
              {ato.revogado_por_numero && <>Revogado por <strong>{ato.revogado_por_numero}</strong>. </>}
              {ato.data_revogacao && <>Em {formatarDataBR(ato.data_revogacao)}. </>}
              {ato.motivo_revogacao && <span>Motivo: {ato.motivo_revogacao}</span>}
            </div>
          </div>
        )}

        {ato.alterado_por_numero && !ato.revogado && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-amber-900 print:hidden">
            <div className="flex items-center gap-2">
              <FiEdit3 size={14} /> Este ato foi alterado por <strong>{ato.alterado_por_numero}</strong>.
            </div>
          </div>
        )}

        {ato.altera_numero && (
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-sm text-blue-900 print:hidden">
            <div className="flex items-center gap-2">
              <FiEdit3 size={14} /> Este ato altera <strong>{ato.altera_numero}</strong>.
            </div>
          </div>
        )}

        <div className="flex items-center gap-2 print:hidden flex-wrap">
          <Button variant="outline" onClick={() => window.print()}>
            <FiPrinter size={14} /> Imprimir versão web
          </Button>
        </div>

        <SectionCard>
          {ato.titulo && (
            <div className="text-center mb-6 pb-4 border-b border-slate-200">
              <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">
                {tipoLabel} nº {ato.numero_publicado}
              </div>
              <h2 className="text-base font-semibold text-slate-900">{ato.titulo}</h2>
            </div>
          )}

          {ato.ementa && (
            <div className="mb-6 pl-6 border-l-2 border-slate-300">
              <p className="text-sm text-slate-700 italic leading-relaxed">{ato.ementa}</p>
            </div>
          )}

          {ato.corpo && (
            <div
              className="prose prose-slate prose-sm max-w-none"
              dangerouslySetInnerHTML={{ __html: ato.corpo }}
            />
          )}

          {(ato.autoridade_nome || ato.autoridade_cargo) && (
            <div className="mt-12 text-center">
              <div className="inline-block">
                <div className="border-t border-slate-400 w-64 mb-1"></div>
                <div className="text-sm font-semibold text-slate-900">{ato.autoridade_nome ?? '—'}</div>
                <div className="text-xs text-slate-600">{ato.autoridade_cargo ?? '—'}</div>
              </div>
            </div>
          )}

          <div className="mt-8 pt-4 border-t border-slate-200 text-xs text-slate-500 print:hidden">
            <div className="flex flex-wrap gap-x-4 gap-y-1">
              {ato.data_publicacao && (
                <span className="inline-flex items-center gap-1">
                  <FiCalendar size={11} /> Publicado em {formatarDataBR(ato.data_publicacao)}
                </span>
              )}
              {ato.autoridade_nome && (
                <span className="inline-flex items-center gap-1">
                  <FiUser size={11} /> {ato.autoridade_nome}
                </span>
              )}
            </div>
          </div>
        </SectionCard>

        <div className="text-xs text-slate-500 text-center print:hidden">
          Documento de transparência pública. Endereço permanente: <span className="font-mono">{window.location.href}</span>
        </div>
      </div>
    </PortalPage>
  );
}

const ERROS: Record<string, string> = {
  nao_encontrado: 'Ato não encontrado ou removido.',
  modulo_invalido: 'Documento inválido.',
  nao_publicado: 'Este ato ainda não foi publicado.',
};

function mensagemErro(c: string): string {
  return ERROS[c] ?? 'Não foi possível carregar o ato.';
}
